/**
 * @fileoverview Modal wrapper for editing assigned students.
 * Layout copied from AssignProjectModal — same height, responsive, styles.
 * No imports from AssignProjectModal to avoid coupling.
 */
import { useDisclosure } from '@mantine/hooks'
import { ChangedDataConfirmModal, Modal } from 'components'
import { useArcTranslation, useMantineTheme, useMediaQuery } from 'hooks'
import type { FC } from 'react'
import { useCallback } from 'react'
import { EditStudentsContent } from './EditStudentsContent'
import { EditStudentsErrorBoundary } from './EditStudentsErrorBoundary'
import type { EditStudentsModalProps } from './types'

/** Copied from AssignProjectModal — independent */
const SPACING = { modal: 24, modalMobile: 16, element: 16 } as const

const getModalStyles = (isMobile: boolean, modalPadding: number) => ({
  content: {
    overflow: 'hidden',
    height: isMobile ? '100%' : '70vh',
    maxHeight: isMobile ? '100%' : '70vh',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  title: { fontSize: isMobile ? 18 : 22, fontWeight: 600, letterSpacing: '-0.3px' },
  header: { padding: `${modalPadding}px ${modalPadding}px ${SPACING.element}px`, marginBottom: 0 },
  body: { padding: 0, display: 'flex' as const, flexDirection: 'column' as const, overflow: 'hidden', flex: 1 },
  close: { color: '#666', '&:hover': { backgroundColor: '#f5f5f5' } },
})

export const EditStudentsModal: FC<EditStudentsModalProps> = ({
  opened, onClose, projectId, schoolTermId, groupName, startDate,
}) => {
  const { t } = useArcTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false
  const modalPadding = isMobile ? SPACING.modalMobile : SPACING.modal
  const modalSize = isMobile ? '100%' : 900
  const modalStyles = getModalStyles(isMobile, modalPadding)
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false)

  const handleConfirmDiscard = useCallback(() => {
    closeConfirm()
    onClose()
  }, [closeConfirm, onClose])

  return (
    <EditStudentsErrorBoundary onClose={onClose}>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t('edit_students.title')}
        size={modalSize}
        fullScreen={isMobile}
        centered={!isMobile}
        padding={0}
        radius={isMobile ? 0 : 'lg'}
        styles={modalStyles}
      >
        <EditStudentsContent
          projectId={projectId}
          schoolTermId={schoolTermId}
          groupName={groupName}
          startDate={startDate}
          onClose={onClose}
          onRequestClose={onClose}
          openConfirm={openConfirm}
        />
      </Modal>
      <ChangedDataConfirmModal opened={confirmOpened} close={closeConfirm} discard={handleConfirmDiscard} />
    </EditStudentsErrorBoundary>
  )
}
